<script>
  import { tick, setContext, getContext, onMount } from "svelte";
  import { writable } from "svelte/store";
  import cloneDeep from "lodash/cloneDeep";
  import isEmpty from "lodash/isEmpty";
  import isEqual from "lodash/isEqual";
  import { navigate } from "svelte-routing";
  import { downloadBlob } from "downloaders";

  import { bucketArrayMultiple } from "overline";
  import { Modal } from "svelte-utilities";
  import ListThumbnail from "#lib/ListThumbnail.svelte";
  import DocumentThumbnail from "#lib/DocumentThumbnail.svelte";
  import SortableThumbnailList from "#lib/SortableThumbnailList.svelte";
  import DownloadIcon from "@local/assets/icons/download.svg";
  import FileMultipleIcon from "@local/assets/icons/file-multiple.svg";
  import FileVirtualIcon from "@local/assets/icons/file-virtual.svg";
  import VideoIcon from "@local/assets/icons/video.svg";
  import ShareIcon from "@local/assets/icons/share.svg";
  import WrenchIcon from "@local/assets/icons/wrench.svg";
  import GearIcon from "@local/assets/icons/gear.svg";
  import SendIcon from "@local/assets/icons/share.svg";
  import SearchSelect from "#lib/sidebar/SearchSelect.svelte";
  import ResizablePanes from "#lib/ResizablePanes.svelte";
  import JobProperties from "#lib/sidebar/ReviewerJobProperties.svelte";
  import DownloadModal from "#lib/home/DownloadModal.svelte";
  import NewDocumentModal from "#lib/NewDocumentModal.svelte";
  import Links from "#lib/sidebar/Links.svelte";
  import SidebarTitle from "#lib/sidebar/SidebarTitle.svelte";
  import ReportList from "#lib/home/ReportList.svelte";

  import { selectedDocuments as selectedDocumentsObj, pathRoot, reviewing } from "#src/stores/ui.js";
  import { orderedList } from "@local/extensions/collections/sortable-list.js";
  import { api, getDocPdf } from "#src/api";
  import { profile } from "#src/stores/auth.js";
  import sanitizePgString from "#src/extensions/sanitize-postgrest-string.js";
  import eb from "#src/extensions/event-bus.js";
  import { discontiguousKey } from "#src/extensions/event-keys.js";
  import { alerts } from "#src/stores/alerts.js";

  export let group;
  export let disabled;
  export let items;
  export let types;
  export let documentNotes;
  export let collections;
  export let standardColumns;
  export let customColumns;
  export let customColColumns;

  let width;
  let height;

  let confirmRemoveModal;
  let confirmShareModal;
  let jobToShare;
  let orgSearchString;
  let selectedOrg;
  let orgSearchResults = [];
  let docsToDelete = [];

  const fileData = writable({});
  const org = getContext("org");

  setContext("fileData", fileData);

  $: attachments = $group.attachments.filter((a) => !a.type_id && !a.item_id);
  $: documents = orderedList($group.documents);
  $: allAttachments = $group && bucketArrayMultiple($group.attachments, "item_id", "type_id");
  $: noAttachments = isEqual(allAttachments, { none: [] });
  $: standardColumns = $org?.data?.standard_columns || {};
  $: capabilities = $profile?.profile_capabilities?.capabilities || {};

  function selectNone() {
    $selectedDocumentsObj = {};
  }

  function selectDocument(e) {
    const { id, event } = e.detail;
    if (event.shiftKey && !isEmpty($selectedDocumentsObj)) {
      const allIds = documents.map((t) => t.id);
      const indices = documents
        .map((t, i) => i)
        .filter((i) => $selectedDocumentsObj[documents[i].id] || documents[i].id === id);

      const min = Math.min(...indices);
      const max = Math.max(...indices);

      const ids = allIds.slice(min, max + 1);
      selectedDocumentsObj.selectOnly(...ids);
    } else if (discontiguousKey(event)) {
      if (!$selectedDocumentsObj[id]) {
        selectedDocumentsObj.select(id);
      } else {
        selectedDocumentsObj.deselect(id);
      }
    } else {
      selectedDocumentsObj.selectOnly(id);
    }
  }

  function confirmRemove(e) {
    const { selected } = e.detail;
    docsToDelete = selected.filter((id) => $group.documents[id]);
    confirmRemoveModal.open();
  }

  async function removeDocuments() {
    const dtd = [...docsToDelete];
    docsToDelete = [];
    await tick();
    group.removeDocument(...dtd);
  }

  function cloneDocuments(d) {
    const { selected } = d.detail;

    const cloned = selected.map((id) => {
      const doc = cloneDeep($group.documents[id]);
      doc.id = crypto.randomUUID();
      return doc;
    });
    group.addDocument(...cloned);
  }

  function updateDocument(e) {
    const { id, diff } = e.detail;
    group.updateDocumentNotUndoable(id, diff);
  }

  function reorder(e) {
    const { insertion, dragging } = e.detail;
    const order = cloneDeep($group.documents.order);
    const moved = order.splice(dragging, 1)[0];
    order.splice(insertion, 0, moved);
    group.updateDocumentOrder(order);
  }

  function nav(e) {
    const { id } = e.detail;
    const doc = $group.documents[id];
    if (!doc) return;

    if (doc.type === "remote") {
      window.open(doc.data.url, "_blank");
    } else {
      const newpath = `${$pathRoot}documents/${id}`;
      navigate(newpath);
    }
  }

  function confirmShare() {
    jobToShare = {
      name: $group.job.name,
      group_id: $group.id,
    };
    confirmShareModal.open();
  }

  async function searchOrgs(e) {
    const name = sanitizePgString(e.detail.value);
    const res = await api
      .from("organizations")
      .select("*")
      .or(`name.ilike.%${name}%,primary_contact_email.ilike.%${name}%`)
      .not("id", "eq", "aa15e3d4-e107-5b40-b034-db88a7d5c03a")
      .not("id", "eq", $org.id)
      .range(0, 5);

    if (res.error) {
      orgSearchResults = [];
    } else {
      orgSearchResults = res.data.map((o) => ({
        ...o,
        cta: o.address,
        type: "existing",
        id: o.id,
      }));
    }
  }

  function getDiff(a, b) {
    const diff = {};
    Object.keys(b).forEach((key) => {
      const aVal = a[key];
      const bVal = b[key];

      if (key === "hidden_columns") {
        const d = getDiff(aVal, bVal);
        diff[key] = d;
      } else {
        if (!isEqual(aVal, bVal)) {
          diff[key] = bVal;
        }
      }
    });
    return diff;
  }

  function selectOrg(e) {
    const result = e.detail.result;
    selectedOrg = result;
    orgSearchString = result.name;
  }

  function deselectOrg() {
    selectedOrg = null;
    orgSearchString = "";
  }

  async function cloneToOrg() {
    const clone = await api.rpc("clone_revision_to_org", {
      group_id: $group.id,
      org_id: selectedOrg.id,
    });
  }

  async function downloadDoc(e) {
    const { record, doneCallback } = e.detail;
    const pdf = await getDocPdf(record.id);
    if (pdf.error) {
      doneCallback();
      alerts.add({
        type: "danger",
        message: "Error downloading PDF",
      });
      console.log(pdf.error);
      return;
    }
    try {
      if (pdf.data.state !== "completed") {
        throw new Error(`PDF failure: ${pdf.data.state}, ${pdf.data.job_id}`);
      }
      const { data, error } = await api.storage.from("temp").download(pdf.data.path);
      if (error) throw error;
      downloadBlob(data, `${pdf.data.name}.pdf`);
    } catch (error) {
      alerts.add({
        type: "danger",
        message: "Error downloading PDF",
      });
      console.log(error);
    }
    doneCallback();
  }

  function beginAddingDocument() {
    eb.dispatch("open-create-document-modal");
  }

  onMount(() => {
    $reviewing = null;
  });
</script>

<div class="w-full h-full flex relative" bind:offsetWidth={width} bind:offsetHeight={height}>
  <ResizablePanes startLeft={850} startBottom={300} showPane={width >= 640} direction="horizontal">
    <div slot="pane" class="w-full h-full flex flex-col bg-white py-4 gap-4 overflow-y-auto">
      <div class="flex justify-between items-center pr-4">
        <div class="grow">
          <SidebarTitle title="Job Properties" />
        </div>
        <div class="flex-none flex gap-2">
          <button class="btn btn-icon" on:click={() => eb.dispatch("open-download")}>
            <DownloadIcon />
          </button>
          {#if $profile?.user_role === "developer"}
            <button class="btn btn-icon" on:click={() => eb.dispatch("open-utilities")}>
              <WrenchIcon />
            </button>
          {/if}
          {#if capabilities.switch_organization}
            <button class="btn btn-icon" on:click={() => eb.dispatch("open-settings")}>
              <GearIcon />
            </button>
            <button class="btn btn-icon" on:click={confirmShare}>
              <SendIcon />
            </button>
          {/if}
        </div>
      </div>
      <JobProperties {disabled} {group} />
      <Links {attachments} {group} {disabled} />
      <ReportList
        {group}
        {items}
        {collections}
        {types}
        {documentNotes}
        {disabled}
        {customColumns}
        {customColColumns} />
    </div>
    <div slot="content" class="bg-gray-200 w-full h-full overflow-y-auto">
      <SortableThumbnailList
        list={documents}
        {disabled}
        selected={$selectedDocumentsObj}
        on:add={beginAddingDocument}
        on:select={selectDocument}
        on:remove-selected={confirmRemove}
        on:select-none={selectNone}
        on:clone-selected={cloneDocuments}
        on:nav={nav}
        on:reorder={reorder}
        let:record>
        <ListThumbnail {record} {group} downloadable={record.type !== "remote"} on:download={downloadDoc}>
          <DocumentThumbnail doc={record} {group} on:update={updateDocument} slot="default" />
          <div slot="caption" class="grow flex justify-between gap-2 overflow-hidden">
            <div class="grow truncate">
              {record.name}
            </div>
            <div>
              {#if record.type === "imported"}
                <FileMultipleIcon />
              {:else if record.type === "virtual"}
                <FileVirtualIcon />
              {:else if record.type === "remote"}
                {#if record.data.doctype === "video"}
                  <VideoIcon />
                {:else if record.data.doctype === "spreadsheet"}
                  <ShareIcon />
                {:else}
                  <ShareIcon />
                {/if}
              {/if}
            </div>
          </div>
        </ListThumbnail>
      </SortableThumbnailList>
    </div>
  </ResizablePanes>
</div>

<DownloadModal title="Download Contents" {group} {types} name={$group.job.name} />
<NewDocumentModal {group} {documents} />

<Modal
  bind:this={confirmRemoveModal}
  on:confirm={removeDocuments}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Delete", type: "confirm", style: "danger" },
  ]}
  closeOnOutclick>
  <div slot="title">Delete Documents</div>
  <div slot="content" class="space-y-2">
    <div class="space-y-2">
      <p>Are you sure you want to delete these documents?</p>
      {#each docsToDelete as id}
        <div class="pl-4 truncate">{$group.documents[id].name}</div>
      {/each}
    </div>
  </div>
</Modal>

{#if capabilities.switch_organization}
  <Modal
    bind:this={confirmShareModal}
    on:confirm={cloneToOrg}
    buttons={[
      { label: "Cancel", type: "cancel" },
      { label: "Copy Job", type: "confirm", disabled: !selectedOrg },
    ]}>
    <div slot="title">Clone Job to Organization</div>
    <div slot="content">
      <div class="mb-2">The following job will be copied:</div>
      <div class="px-4 mb-2">
        {jobToShare?.name}
      </div>
      <SearchSelect
        placeholder="Destination Organization"
        border
        bind:value={orgSearchString}
        selection={selectedOrg}
        results={orgSearchResults}
        on:focus={searchOrgs}
        on:search={searchOrgs}
        on:select={selectOrg}
        on:deselect={deselectOrg} />
    </div>
  </Modal>
{/if}
